import React from 'react'
import styled, { createGlobalStyle, css } from 'styled-components'
import { LayerConfigInterface } from '../types'
import LiveTooltip from 'live/components/ui/tooltip'
import i18n from 'live/services/i18n'
import { DEFAULT_FONT_SIZE } from '../constants'

type widgetValueProps = {
    index: number
    layerConfig: LayerConfigInterface
    events: any
}

export const WidgetValue = ({ index, layerConfig, events }: widgetValueProps) => {
    const { mainValue, secondaryValues } = layerConfig
    const EVENT_FONT_COLOR = events?.__color
    const EVENT_FONT_SIZE = events?.__fontsize

    const trendMarkColor = events?.__trendMarkColor
    const trendMarkMainValue = events?.__trendMark?.[mainValue?.variable.value]?.toLowerCase()
    const trendMarkTooltipMainValue = events?.__trendMarkTooltip?.[mainValue?.variable.value]
    const configTooltipContentMainValue = mainValue?.trend?.tooltipMessage || ''

    return (
        <Container fontSize={EVENT_FONT_SIZE || DEFAULT_FONT_SIZE}>
            <GlobalStyles />
            <MainValueContainer
                labelPosition={mainValue?.label?.position}
                fontSize={EVENT_FONT_SIZE || DEFAULT_FONT_SIZE}
            >
                <LabelStyled
                    className="label"
                    isEnabled={mainValue?.label?.isEnabled}
                    fontColor={EVENT_FONT_COLOR || mainValue?.label?.fontColor}
                    isBold={mainValue?.label?.isBold}
                    isItalic={mainValue?.label?.isItalic}
                >
                    <IconStyled
                        className={`far fa-${mainValue?.label.iconValue} labelIcon`}
                        isEnabled={mainValue?.label?.isIconEnabled}
                        fontColor={EVENT_FONT_COLOR || mainValue?.label?.iconColor}
                        fontSize={EVENT_FONT_SIZE || DEFAULT_FONT_SIZE}
                    />
                    <span>{mainValue?.label.value}</span>
                </LabelStyled>

                <VariableContainer fontSize={EVENT_FONT_SIZE || DEFAULT_FONT_SIZE}>
                    <TrendStyled
                        className="trend"
                        isEnabled={trendMarkMainValue && mainValue?.trend?.isEnabled}
                        fontColor={trendMarkColor?.[trendMarkMainValue] || EVENT_FONT_COLOR}
                    >
                        <LiveTooltip
                            position="top"
                            content={i18n(configTooltipContentMainValue + ' ' + trendMarkTooltipMainValue)}
                            hide={
                                !(configTooltipContentMainValue?.length > 0) && !(trendMarkTooltipMainValue?.length > 0)
                            }
                        >
                            <i className={`far fa-arrow-${trendMarkMainValue} arrow`} />
                        </LiveTooltip>
                    </TrendStyled>

                    <PrefixAndSufixStyled
                        className="prefix"
                        isEnabled={mainValue?.prefix?.isEnabled}
                        fontColor={EVENT_FONT_COLOR || mainValue?.prefix?.fontColor}
                        isBold={mainValue?.prefix?.isBold}
                        isItalic={mainValue?.prefix?.isItalic}
                    >
                        {mainValue?.prefix.value}
                    </PrefixAndSufixStyled>

                    <VariableStyled
                        className="variable"
                        fontColor={EVENT_FONT_COLOR || mainValue?.variable?.fontColor}
                        isBold={mainValue?.variable?.isBold}
                        isItalic={mainValue?.variable?.isItalic}
                    >
                        {events?.[mainValue?.variable.value]}
                    </VariableStyled>

                    <PrefixAndSufixStyled
                        className="sufix"
                        isEnabled={mainValue?.sufix?.isEnabled}
                        fontColor={EVENT_FONT_COLOR || mainValue?.sufix?.fontColor}
                        isBold={mainValue?.sufix?.isBold}
                        isItalic={mainValue?.sufix?.isItalic}
                    >
                        {mainValue?.sufix?.value}
                    </PrefixAndSufixStyled>
                </VariableContainer>
            </MainValueContainer>

            {secondaryValues?.map((secondaryValue, index) => {
                const trendMarkSecValue = events?.__trendMark?.[secondaryValue?.variable.value]?.toLowerCase()
                const trendMarkTooltipSecValue = events?.__trendMarkTooltip?.[secondaryValue?.variable.value] || ''
                const configTooltipContentSecValue = secondaryValue?.trend?.tooltipMessage || ''

                return (
                    <SecValueContainer
                        key={index}
                        labelPosition={secondaryValue?.label?.position}
                        fontSize={EVENT_FONT_SIZE || DEFAULT_FONT_SIZE}
                    >
                        <LabelStyled
                            className="label"
                            isEnabled={secondaryValue?.label?.isEnabled}
                            fontColor={EVENT_FONT_COLOR || secondaryValue?.label?.fontColor}
                            isBold={secondaryValue?.label?.isBold}
                            isItalic={secondaryValue?.label?.isItalic}
                        >
                            <IconStyled
                                className={`far fa-${secondaryValue?.label.iconValue} labelIcon`}
                                isEnabled={secondaryValue?.label?.isIconEnabled}
                                fontColor={EVENT_FONT_COLOR || secondaryValue?.label?.iconColor}
                                fontSize={EVENT_FONT_SIZE || DEFAULT_FONT_SIZE}
                            />
                            {secondaryValue.label.value}
                        </LabelStyled>

                        <VariableContainer fontSize={EVENT_FONT_SIZE || DEFAULT_FONT_SIZE}>
                            <TrendStyled
                                className="trend"
                                isEnabled={trendMarkSecValue && secondaryValue?.trend?.isEnabled}
                                fontColor={trendMarkColor?.[trendMarkSecValue] || EVENT_FONT_COLOR}
                            >
                                <LiveTooltip
                                    position="top"
                                    content={i18n(configTooltipContentSecValue + ' ' + trendMarkTooltipSecValue)}
                                    hide={
                                        !(configTooltipContentSecValue?.length > 0) &&
                                        !(trendMarkTooltipSecValue?.length > 0)
                                    }
                                >
                                    <i className={`far fa-arrow-${trendMarkSecValue} arrow`} />
                                </LiveTooltip>
                            </TrendStyled>

                            <PrefixAndSufixStyled
                                className="prefix"
                                isEnabled={secondaryValue?.prefix?.isEnabled}
                                fontColor={EVENT_FONT_COLOR || secondaryValue?.prefix?.fontColor}
                                isBold={secondaryValue?.prefix?.isBold}
                                isItalic={secondaryValue?.prefix?.isItalic}
                            >
                                {secondaryValue.prefix.value}
                            </PrefixAndSufixStyled>

                            <VariableStyled
                                className="variable"
                                fontColor={EVENT_FONT_COLOR || secondaryValue?.variable?.fontColor}
                                isBold={secondaryValue?.variable?.isBold}
                                isItalic={secondaryValue?.variable?.isItalic}
                            >
                                {events?.[secondaryValue?.variable.value]}
                            </VariableStyled>

                            <PrefixAndSufixStyled
                                className="sufix"
                                isEnabled={secondaryValue?.sufix?.isEnabled}
                                fontColor={EVENT_FONT_COLOR || secondaryValue?.sufix?.fontColor}
                                isBold={secondaryValue?.sufix?.isBold}
                                isItalic={secondaryValue?.sufix?.isItalic}
                            >
                                {secondaryValue.sufix.value}
                            </PrefixAndSufixStyled>
                        </VariableContainer>
                    </SecValueContainer>
                )
            })}
        </Container>
    )
}

const PrefixAndSufixStyled = styled.span<{
    isEnabled: boolean
    fontColor: string
    isBold: boolean
    isItalic: boolean
}>`
    display: ${({ isEnabled }) => !isEnabled && 'none'} !important;
    color: ${({ fontColor }) => (fontColor ? fontColor : '')};
    font-weight: ${({ isBold }) => isBold && 'bold'};
    font-style: ${({ isItalic }) => isItalic && 'italic'};
`

const LabelStyled = styled.div<{
    isEnabled: boolean
    fontColor: string
    isBold: boolean
    isItalic: boolean
}>`
    display: ${({ isEnabled }) => (!isEnabled ? 'none !important' : 'flex')};
    color: ${({ fontColor }) => (fontColor ? fontColor : '')};
    font-weight: ${({ isBold }) => isBold && 'bold'};
    font-style: ${({ isItalic }) => isItalic && 'italic'};
    align-items: baseline;
    width: max-content;

    i {
        align-self: center;
    }
`

const VariableStyled = styled.span<{
    fontColor: string
    isBold: boolean
    isItalic: boolean
}>`
    color: ${({ fontColor }) => (fontColor ? fontColor : '')};
    font-weight: ${({ isBold }) => isBold && 'bold'};
    font-style: ${({ isItalic }) => isItalic && 'italic'};
`

const IconStyled = styled.i<{
    isEnabled: boolean
    fontColor: string
    fontSize: string
}>`
    display: ${({ isEnabled }) => !isEnabled && 'none'} !important;
    color: ${({ fontColor }) => (fontColor ? fontColor : '')};
    ${({ fontSize }) => css`
        padding-right: calc(${fontSize} / 12);
    `}
`

const TrendStyled = styled.span<{
    isEnabled: boolean
    fontColor: string
}>`
    display: ${({ isEnabled }) => !isEnabled && 'none'} !important;
    color: ${({ fontColor }) => (fontColor ? fontColor : '')};

    > i {
        display: inline;
        line-height: inherit;
    }
`

const Container = styled.div<{ fontSize: string }>`
    display: flex;
    flex-direction: column;

    ${({ fontSize }) => css`
        padding: calc(${fontSize} / 3);
    `}

    > div {
        align-items: baseline;
    }
`

const VariableContainer = styled.div<{ fontSize: string }>`
    display: inline;
    line-height: 100%;
    white-space: nowrap;

    ${({ fontSize }) => css`
        .prefix,
        .trend {
            padding-right: calc(${fontSize} / 9);
        }
        .sufix {
            padding-left: 2px;
        }
    `}
`

const MainValueContainer = styled.div<{
    labelPosition?: string
    fontSize: string
}>`
    align-items: flex-end;
    justify-content: flex-start;
    display: flex;
    line-height: normal;

    ${({ fontSize }) => css`
        margin-bottom: calc(${fontSize} / 2);

        .variable {
            font-size: ${fontSize};
        }

        .prefix,
        .sufix,
        .label {
            font-size: calc(${fontSize} / 2);
        }

        .labelIcon {
            font-size: calc(${fontSize} / 3);
        }

        .trend {
            font-size: calc(${fontSize} * 2 / 3);
        }
    `}

    ${({ labelPosition, fontSize }) =>
        labelPosition === 'top'
            ? css`
                  flex-direction: column;
                  align-items: flex-start;
                  .label {
                      margin-bottom: calc(${fontSize} / 3);
                  }
              `
            : labelPosition === 'right'
            ? css`
                  flex-direction: row-reverse;
                  justify-content: flex-end;
                  .label {
                      margin-left: calc(${fontSize} / 3);
                  }
              `
            : labelPosition === 'bottom'
            ? css`
                  flex-direction: column-reverse;
                  align-items: flex-start;
                  .label {
                      margin-top: 2px;
                  }
              `
            : css`
                  //labelPosition === 'left'
                  flex-direction: row;
                  .label {
                      margin-right: calc(${fontSize} / 6);
                  }
              `}
`

const SecValueContainer = styled.div<{
    labelPosition?: string
    fontSize: string
}>`
    align-items: flex-end;
    justify-content: flex-start;
    display: flex;
    line-height: normal;
    /* margin-bottom: 12px; */

    ${({ fontSize }) => css`
        margin-bottom: calc(${fontSize} / 4);

        .variable {
            font-size: calc(${fontSize} / 2);
        }

        .prefix,
        .sufix,
        .label,
        .trend {
            font-size: calc(${fontSize} / 3);
        }

        .labelIcon {
            font-size: calc(${fontSize} / 4);
        }
    `}

    ${({ labelPosition, fontSize }) =>
        labelPosition === 'top'
            ? css`
                  flex-direction: column;
                  align-items: flex-start;
                  .label {
                      margin-bottom: calc(${fontSize} / 6);
                  }
              `
            : labelPosition === 'right'
            ? css`
                  flex-direction: row-reverse;
                  justify-content: flex-end;
                  .label {
                      margin-left: calc(${fontSize} / 4);
                  }
              `
            : labelPosition === 'bottom'
            ? css`
                  flex-direction: column-reverse;
                  align-items: flex-start;
                  margin-top: calc(${fontSize} / 6);
                  .label {
                      margin-top: 2px;
                  }
              `
            : css`
                  //labelPosition === 'left'
                  flex-direction: row;
                  .label {
                      margin-right: calc(${fontSize} / 9);
                  }
              `}
`

export const GlobalStyles = createGlobalStyle`
    .default-editor .widget-footer {
        pointer-events: none;
    }
`
